import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import type { RxCollection } from 'rxdb'
import languagesJson from 'shared/languages'
import { Button } from 'ui/button'
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList } from 'ui/combobox'
import { Label } from 'ui/label'
import { cn } from 'ui/lib/utils'
import { toast } from 'ui/sonner'

import { apiClient } from '~/api/client'
import type { Card } from '~/db/types'
import { useAccountStore } from '~/stores/account'
import { useCardsGameStore } from '~/stores/cards-game'
import { useSettingsStore } from '~/stores/settings'
import type { TRoutes } from '~/types/routes'
import { buildListQuery } from '~/utils/build-list-query'
import { syncPushSubscriptionLanguages } from '~/utils/sync-push-subscription-languages'

type LanguageItem = {
  label: string
  value: string
}

type LanguageSelectProps = {
  cardsCollection?: RxCollection<Card> | null
  className?: string
  defaultLearnLanguage?: LanguageItem
  defaultSourceLanguage?: LanguageItem
  navigateTo?: TRoutes
}

export const LanguageSelect = ({
  defaultLearnLanguage,
  defaultSourceLanguage,
  className,
  navigateTo,
  cardsCollection,
}: LanguageSelectProps) => {
  const languages = useMemo<LanguageItem[]>(
    () =>
      Object.keys(languagesJson).map(key => ({ value: key, label: languagesJson[key as keyof typeof languagesJson] })),
    [],
  )
  const [sourceLanguage, setSourceLanguage] = useState<LanguageItem | null>(() => {
    if (defaultSourceLanguage) return defaultSourceLanguage

    const userLanguage = navigator.language.split('-')[0]

    return languages.find(l => l.value === userLanguage) ?? null
  })
  const [learnLanguage, setLearnLanguage] = useState<LanguageItem | null>(() => {
    if (defaultLearnLanguage) return defaultLearnLanguage

    const userLanguage = navigator.language.split('-')[0]
    if (userLanguage === 'en') return null

    return languages.find(l => l.value === 'en') ?? null
  })
  const addLanguagePair = useAccountStore(state => state.addLanguagePair)
  const patchSettings = useSettingsStore(state => state.patchSettings)
  const navigate = useNavigate()
  const resetGameState = useCardsGameStore(state => state.reset)
  const accountId = useAccountStore(state => state.id)

  const { t } = useTranslation()

  const hasSameLanguageSelected = !!sourceLanguage && !!learnLanguage && sourceLanguage.value === learnLanguage.value
  const canSave = !!sourceLanguage && !!learnLanguage && !hasSameLanguageSelected

  return (
    <form
      className={cn('flex flex-col gap-4 overflow-visible', className)}
      onSubmit={async event => {
        event.preventDefault()
        if (!canSave) return

        patchSettings({
          sourceLanguage,
          learnLanguage,
        })
        void cardsCollection
          ?.find({ selector: buildListQuery(accountId, learnLanguage.value, sourceLanguage.value) })
          .exec()
          .then(resetGameState)

        addLanguagePair(sourceLanguage.value, learnLanguage.value)

        try {
          // TODO: refactor to make it more offline-friendly
          await apiClient.PATCH('/user/languages', {
            body: { source: sourceLanguage.value, learn: learnLanguage.value },
          })
          await syncPushSubscriptionLanguages()
        } catch (error) {
          toast.error(t('Network error'))
          console.error({ message: 'Network error', error })
        }

        if (navigateTo) void navigate({ to: navigateTo })
      }}
    >
      <div className="space-y-1">
        <Label htmlFor="learn-language">{t('Language to learn')}</Label>
        <Combobox
          itemToStringLabel={(item: LanguageItem) => item.label}
          items={languages}
          onValueChange={setLearnLanguage}
          value={learnLanguage}
        >
          <ComboboxInput id="learn-language" placeholder={t('Select a language to learn')} />
          <ComboboxContent>
            <ComboboxEmpty>{t('No items found')}</ComboboxEmpty>
            <ComboboxList>
              {(item: LanguageItem) => (
                <ComboboxItem key={item.value} value={item}>
                  {item.label}
                </ComboboxItem>
              )}
            </ComboboxList>
          </ComboboxContent>
        </Combobox>
      </div>
      <div className="space-y-1">
        <Label htmlFor="source-language">{t('Source language')}</Label>
        <Combobox
          itemToStringLabel={(item: LanguageItem) => item.label}
          items={languages}
          onValueChange={setSourceLanguage}
          value={sourceLanguage}
        >
          <ComboboxInput id="source-language" placeholder={t('Select source language')} />
          <ComboboxContent>
            <ComboboxEmpty>{t('No items found')}</ComboboxEmpty>
            <ComboboxList>
              {(item: LanguageItem) => (
                <ComboboxItem key={item.value} value={item}>
                  {item.label}
                </ComboboxItem>
              )}
            </ComboboxList>
          </ComboboxContent>
        </Combobox>
      </div>
      {hasSameLanguageSelected && (
        <p className="text-sm text-destructive">{t('Source and learn languages must be different')}</p>
      )}
      <Button disabled={!canSave} type="submit">
        {t('Save')}
      </Button>
    </form>
  )
}
